import { FormikHelpers } from "formik";
import React from "react";
import styled from "styled-components";
import { Button } from "./Button";
import { Icon, IconSvg } from "./Icon";
import { Text } from "./Text";
import { TextInput, Input } from "./TextInput";
import { ErrorMessage, InputWrapper } from "./TextInputUtils";
import { style } from "./style";

/**
 * -------------------- Input Stepper: Future Enhancements --------------------
 *
 * 1. Compact Version - Stepper buttons are stacked on top of each other
 * 2. Unit Label - Embedding units within the input
 *     - We can consider adding padding within the input and using a negative
 *       margin to get the label "within" the input
 *     - We can just put the unit label above the Stepper, similar to what we
 *       do with the TextInput or SelectMenu components.
 * 3. Non-Formik Contexts - Right now, "setFieldValue" is a mandatory parameter
 */

const InputStepperContainer = styled.div`
  max-width: 200px;
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

type HasErrorProps = {
  $hasError?: boolean;
};

const StepperButton = styled(Button)<HasErrorProps>`
  border-color: ${(props) => (props.$hasError ? style.colors.red.dark : style.colors.grey.dark)};
  border-width: ${style.vars.borderWidth.thin};
  min-height: ${style.vars.height.base};
  min-width: ${style.vars.height.base};
  transition: border-color ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};

  ${IconSvg} {
    fill: ${style.colors.grey.dark};
    transition: fill ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};
  }

  &:focus,
  &:hover {
    background-color: inherit;
    border-color: ${style.colors.blue.dark};

    ${IconSvg} {
      fill: ${style.colors.blue.dark};
    }
  }

  &:active {
    background-color: ${style.colors.blue.lightest};
    border-color: ${style.colors.blue.darkest};
    transition: none;

    ${IconSvg} {
      fill: ${style.colors.blue.darkest};
      transition: none;
    }
  }

  &:after {
    z-index: ${style.vars.zIndex.raised};
  }
`;

const LeftStepperButton = styled(StepperButton)`
  border-radius: ${style.vars.borderRadius.base} 0 0 ${style.vars.borderRadius.base};
  border-right: 0;
`;

const RightStepperButton = styled(StepperButton)`
  border-radius: 0 ${style.vars.borderRadius.base} ${style.vars.borderRadius.base} 0;
  border-left: 0;
`;

const StyledTextInput = styled(TextInput)<HasErrorProps>`
  ${InputWrapper} {
    margin-top: 0;
    min-width: 0;
  }

  ${Input} {
    border-color: ${(props) => (props.$hasError ? style.colors.red.dark : style.colors.grey.dark)};
    border-radius: 0;
    text-align: center;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }
    appearance: textfield;
  }
`;

const StepperText = styled(Text).attrs({ type: "small" })`
  color: ${style.colors.grey.darkest};
  font-weight: ${style.vars.fontWeight.base};
`;

const ErrorText = styled(Text).attrs({ type: "small" })`
  color: ${style.colors.red.dark};
  font-weight: ${style.vars.fontWeight.bold};
`;

const ErrorIcon = styled(Icon).attrs({ large: true, icon: "error" })`
  margin-right: ${style.vars.whitespace.xsmall};
`;

export type Props = React.HTMLAttributes<HTMLInputElement> & {
  name: string;
  value: number;
  setFieldValue: FormikHelpers<any>["setFieldValue"];
  min: number;
  max?: number;
  disableDecrement?: boolean;
  disableIncrement?: boolean;
  maxText?: string;
  className?: string;
  error?: string;
};

/**
 * @deprecated Use `StepperV2` instead
 */
export const InputStepper: React.FC<Props> = ({
  name,
  value,
  setFieldValue,
  min,
  max = Infinity,
  disableDecrement = false,
  disableIncrement = false,
  maxText = "You've reached the maximum",
  className,
  error,
  ...props
}) => {
  const currentValue = Number(value);
  return (
    <InputStepperContainer className={className}>
      <FlexWrapper className={className}>
        <LeftStepperButton
          $hasError={!!error}
          aria-label="decrease"
          icon="minus"
          variation="icon"
          className={className}
          disabled={currentValue <= min || disableDecrement}
          onClick={() => {
            currentValue > max ? setFieldValue(name, max) : setFieldValue(name, currentValue - 1);
          }}
        />
        <StyledTextInput
          $hasError={!!error}
          aria-label="quantity"
          name={name}
          type="number"
          min={min}
          max={max}
          value={value}
          autoComplete="off"
          className={className}
          {...props}
        />
        <RightStepperButton
          $hasError={!!error}
          aria-label="increase"
          icon="plus"
          variation="icon"
          className={className}
          disabled={currentValue >= max || disableIncrement}
          onClick={() => {
            currentValue < min ? setFieldValue(name, min) : setFieldValue(name, currentValue + 1);
          }}
        />
      </FlexWrapper>
      {maxText && currentValue === max && !error && <StepperText>{maxText}</StepperText>}
      {error && (
        <ErrorMessage>
          <ErrorIcon />
          <ErrorText>{error}</ErrorText>
        </ErrorMessage>
      )}
    </InputStepperContainer>
  );
};
